import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Clock, Send, ChevronDown, ChevronUp, ChevronLeft, Home } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { OposaiLogo } from '@/components/TuPlazaIALogo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ExamPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const mode = location.state?.mode || 'quick';
  
  const [stage, setStage] = useState('setup');
  const [subjects, setSubjects] = useState([]);
  const [selectedTopics, setSelectedTopics] = useState([]);
  const [expandedSubjects, setExpandedSubjects] = useState({});
  const [numQuestions, setNumQuestions] = useState(10);
  const [examSessionId, setExamSessionId] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadSubjectsWithTopics();
  }, []);

  useEffect(() => {
    if (timeLeft === null || timeLeft <= 0) return;
    const timer = setInterval(() => {
      setTimeLeft((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [timeLeft]);

  const loadSubjectsWithTopics = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/subjects-with-topics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Extract subjects array from response
      const subjectsData = response.data.subjects || response.data || [];
      setSubjects(Array.isArray(subjectsData) ? subjectsData : []);
    } catch (error) {
      console.error('Error loading subjects:', error);
      toast.error('Error al cargar asignaturas');
      setSubjects([]);
    }
  };

  const toggleSubjectExpand = (subjectId) => {
    setExpandedSubjects(prev => ({
      ...prev,
      [subjectId]: !prev[subjectId]
    }));
  };

  const toggleTopic = (topicId) => {
    if (selectedTopics.includes(topicId)) {
      setSelectedTopics(selectedTopics.filter(id => id !== topicId));
    } else {
      setSelectedTopics([...selectedTopics, topicId]);
    }
  };

  const selectAllTopicsInSubject = (subject) => {
    const topicIds = subject.topics.map(t => t.topic_id);
    const allSelected = topicIds.every(id => selectedTopics.includes(id));
    
    if (allSelected) {
      setSelectedTopics(selectedTopics.filter(id => !topicIds.includes(id)));
    } else {
      const newSelected = [...selectedTopics, ...topicIds.filter(id => !selectedTopics.includes(id))];
      setSelectedTopics(newSelected);
    }
  };

  const startExam = async () => {
    if (mode === 'quick' && selectedTopics.length === 0) {
      toast.error('Selecciona al menos un tema');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('session_token');
      const requestData = {
        exam_type: mode,
        topic_ids: mode === 'simulacro' ? [] : selectedTopics,
        num_questions: mode === 'simulacro' ? 110 : numQuestions,  // 100 + 10 reserve
      };
      
      const response = await axios.post(
        `${API}/exam/start`,
        requestData,
        { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 }
      );
      setExamSessionId(response.data.exam_session_id);
      setQuestions(response.data.questions);
      setStage('exam');
      if (mode === 'simulacro') {
        setTimeLeft(150 * 60);
      }
      toast.success('¡Examen iniciado!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al iniciar examen');
    } finally {
      setLoading(false);
    }
  };

  const submitExam = async () => {
    const answeredCount = Object.keys(answers).length;
    const blankCount = questions.length - answeredCount;
    
    if (blankCount > 0) {
      const confirmMessage = mode === 'simulacro' 
        ? `Tienes ${blankCount} preguntas sin responder. Las preguntas en blanco NO restan puntos, pero las incorrectas SÍ penalizan según la configuración del examen. ¿Deseas enviar el examen?`
        : `Solo has respondido ${answeredCount} de ${questions.length} preguntas. ¿Deseas enviar el examen?`;
      
      if (!window.confirm(confirmMessage)) {
        return;
      }
    }

    setLoading(true);
    toast.info('Analizando tus respuestas con IA...');
    
    try {
      const token = localStorage.getItem('session_token');
      
      // Crear array completo de respuestas con índices (incluyendo blancos)
      const answerArray = questions.map((question, index) => {
        const selectedIndex = answers[question.question_id];
        let selectedOptionId = null;
        
        if (selectedIndex !== undefined && selectedIndex !== null) {
          // Las opciones pueden ser strings o objetos
          const options = question.options || [];
          if (options.length > selectedIndex) {
            const option = options[selectedIndex];
            // Si es un objeto con option_id, usarlo; si no, usar el índice
            selectedOptionId = typeof option === 'object' && option.option_id 
              ? option.option_id 
              : selectedIndex;
          }
        }
        
        return {
          question_id: question.question_id,
          selected_option_id: selectedOptionId,
          question_index: index + 1
        };
      });
      
      console.log('Submitting answers:', answerArray); // Debug
      
      const response = await axios.post(
        `${API}/exam/submit`,
        { exam_session_id: examSessionId, answers: answerArray },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 120000 }
      );
      
      toast.success('¡Análisis completado!');
      navigate(`/results/${examSessionId}`, { state: { results: response.data } });
    } catch (error) {
      console.error('Error submitting exam:', error);
      toast.error(error.response?.data?.detail || 'Error al enviar examen. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (stage === 'setup') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50">
        {/* Header with back button */}
        <nav className="border-b border-slate-200 bg-white/70 backdrop-blur-xl sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between h-16 items-center">
              <div className="flex items-center gap-4">
                <button 
                  onClick={() => navigate('/dashboard')}
                  className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
                >
                  <ChevronLeft className="w-5 h-5" />
                  <span className="text-sm font-medium">Volver al Dashboard</span>
                </button>
              </div>
              <OposaiLogo size={36} />
            </div>
          </div>
        </nav>

        <div className="flex items-center justify-center p-4 py-12">
        <div className="w-full max-w-2xl bg-white rounded-2xl shadow-lg border border-slate-200 p-8">
          <h1 className="text-3xl font-extrabold font-heading mb-2">
            {mode === 'quick' ? 'Test Rápido' : 'Simulacro Completo'}
          </h1>
          <p className="text-slate-600 mb-8">
            {mode === 'quick'
              ? 'Configura tu test rápido eligiendo temas y número de preguntas (máx. 60)'
              : 'Prepárate para un simulacro oficial: 100 preguntas + 10 de reserva con temporizador de 150 minutos. Las respuestas incorrectas restan. Puedes dejar preguntas en blanco.'}
          </p>

          <div className="space-y-6">
            {mode === 'quick' && (
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-2 block">Selecciona temas para tu test</Label>
                <p className="text-xs text-slate-500 mb-3">Puedes seleccionar temas individuales o asignaturas completas</p>
                <div className="space-y-3 max-h-[500px] overflow-y-auto border border-slate-200 rounded-xl p-4">
                  {subjects.map((subject) => {
                    const topicIds = subject.topics?.map(t => t.topic_id) || [];
                    const selectedCount = topicIds.filter(id => selectedTopics.includes(id)).length;
                    const isAllSelected = topicIds.length > 0 && selectedCount === topicIds.length;
                    const isPartialSelected = selectedCount > 0 && selectedCount < topicIds.length;
                    const isExpanded = expandedSubjects[subject.subject_id];
                    
                    return (
                      <div key={subject.subject_id} className="border border-slate-100 rounded-lg overflow-hidden">
                        {/* Subject header */}
                        <div className="flex items-center gap-3 p-3 bg-slate-50 hover:bg-slate-100 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isAllSelected}
                            ref={el => { if (el) el.indeterminate = isPartialSelected; }}
                            onChange={() => selectAllTopicsInSubject(subject)}
                            className="w-5 h-5 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
                          />
                          <div className="flex-1" onClick={() => toggleSubjectExpand(subject.subject_id)}>
                            <span className="font-semibold text-slate-900">{subject.name}</span>
                            <span className="ml-2 text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">
                              {selectedCount}/{topicIds.length} temas
                            </span>
                          </div>
                          <button 
                            onClick={() => toggleSubjectExpand(subject.subject_id)}
                            className="p-1 hover:bg-slate-200 rounded"
                          >
                            {isExpanded ? <ChevronUp className="w-5 h-5 text-slate-500" /> : <ChevronDown className="w-5 h-5 text-slate-500" />}
                          </button>
                        </div>
                        
                        {/* Individual topics */}
                        {isExpanded && subject.topics && (
                          <div className="p-3 bg-white border-t border-slate-100">
                            <div className="grid grid-cols-1 gap-2">
                              {subject.topics.map((topic) => (
                                <label 
                                  key={topic.topic_id} 
                                  className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-all ${
                                    selectedTopics.includes(topic.topic_id) 
                                      ? 'bg-indigo-50 border border-indigo-200' 
                                      : 'hover:bg-slate-50 border border-transparent'
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={selectedTopics.includes(topic.topic_id)}
                                    onChange={() => toggleTopic(topic.topic_id)}
                                    className="w-4 h-4 text-indigo-600 border-slate-300 rounded focus:ring-indigo-500"
                                  />
                                  <span className="text-sm text-slate-700">{topic.name}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {selectedTopics.length > 0 && (
                  <p className="mt-2 text-sm text-indigo-600 font-medium">
                    {selectedTopics.length} tema(s) seleccionado(s)
                  </p>
                )}
              </div>
            )}

            {mode === 'simulacro' && (
              <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-6">
                <h3 className="font-bold text-indigo-900 mb-2">Simulacro Oficial Completo</h3>
                <ul className="text-sm text-indigo-700 space-y-2">
                  <li>✓ <strong>100 preguntas oficiales + 10 de reserva</strong></li>
                  <li>✓ Incluye TODAS las asignaturas y temas</li>
                  <li>✓ Temporizador de 150 minutos (2h 30min)</li>
                  <li>✓ <strong>Las respuestas incorrectas restan puntos</strong></li>
                  <li>✓ <strong>Puedes dejar preguntas en blanco</strong> (no restan)</li>
                  <li>✓ Condiciones reales de examen OPE</li>
                </ul>
                
                {/* Show all subjects and topics */}
                <div className="mt-4 pt-4 border-t border-indigo-200">
                  <p className="text-sm font-medium text-indigo-900 mb-3">Temario completo incluido:</p>
                  {subjects.map((subject) => (
                    <div key={subject.subject_id} className="mb-3">
                      <p className="text-sm font-medium text-indigo-800">{subject.name} ({subject.topic_count || subject.topics?.length || 0} temas)</p>
                      {subject.topics && (
                        <div className="ml-4 mt-1 text-xs text-indigo-600">
                          {subject.topics.slice(0, 5).map((t, i) => (
                            <span key={t.topic_id}>{t.name}{i < Math.min(4, subject.topics.length - 1) ? ', ' : ''}</span>
                          ))}
                          {subject.topics.length > 5 && <span> ... y {subject.topics.length - 5} más</span>}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {mode === 'quick' && (
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-2 block">Número de preguntas</Label>
                <Select value={numQuestions.toString()} onValueChange={(value) => setNumQuestions(parseInt(value))}>
                  <SelectTrigger className="rounded-xl border-slate-200" data-testid="num-questions-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10 preguntas</SelectItem>
                    <SelectItem value="20">20 preguntas</SelectItem>
                    <SelectItem value="30">30 preguntas</SelectItem>
                    <SelectItem value="40">40 preguntas</SelectItem>
                    <SelectItem value="50">50 preguntas</SelectItem>
                    <SelectItem value="60">60 preguntas</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <Button
              onClick={startExam}
              disabled={loading || (mode === 'quick' && selectedTopics.length === 0)}
              className="w-full rounded-full bg-indigo-600 hover:bg-indigo-700 text-white py-6 text-lg font-bold shadow-lg"
              data-testid="start-exam-btn"
            >
              {loading ? 'Cargando...' : mode === 'simulacro' ? 'Comenzar Simulacro Oficial' : 'Iniciar Test Rápido'}
            </Button>
          </div>
        </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="sticky top-0 z-50 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-3xl mx-auto px-4 py-4 flex justify-between items-center">
          {/* Back button */}
          <button 
            onClick={() => {
              if (window.confirm('¿Seguro que quieres salir? Perderás el progreso del examen.')) {
                navigate('/dashboard');
              }
            }}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
          >
            <Home className="w-5 h-5" />
            <span className="text-sm font-medium hidden sm:inline">Salir</span>
          </button>
          
          <div className="flex items-center gap-4">
            <div>
              <span className="text-sm font-medium text-slate-600">Respondidas</span>
              <span className="text-lg font-bold text-indigo-600 ml-2">
                {Object.keys(answers).length} / {questions.length}
              </span>
            </div>
            {timeLeft !== null && (
              <div className="flex items-center gap-2 px-4 py-2 bg-indigo-50 rounded-full">
                <Clock className="w-5 h-5 text-indigo-600" />
                <span className="font-bold text-indigo-600">{formatTime(timeLeft)}</span>
              </div>
            )}
          </div>
          
          <Button
            onClick={submitExam}
            disabled={loading}
            className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-6 font-bold"
            data-testid="submit-exam-btn"
          >
            <Send className="w-4 h-4 mr-2" />
            Enviar
          </Button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8 space-y-8">
        {questions.map((question, index) => (
          <div key={question.question_id} className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm" data-testid={`question-${index}`}>
            <div className="flex items-start gap-4 mb-6">
              <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                <span className="font-bold text-indigo-600">{index + 1}</span>
              </div>
              <p className="text-lg text-slate-900 flex-1">{question.question_text || question.question || question.text}</p>
            </div>

            <RadioGroup
              value={answers[question.question_id]?.toString() || ''}
              onValueChange={(value) => setAnswers({ ...answers, [question.question_id]: parseInt(value) })}
            >
              <div className="space-y-3">
                {(question.options || []).map((option, optIdx) => {
                  // Handle both formats: array of strings or array of objects
                  const optionText = typeof option === 'string' ? option : (option.option_text || option.text);
                  const optionId = typeof option === 'string' ? optIdx : (option.option_id || optIdx);
                  
                  return (
                    <label
                      key={optionId}
                      className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                        answers[question.question_id] === optIdx
                          ? 'border-indigo-600 bg-indigo-50'
                          : 'border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/50'
                      }`}
                      data-testid={`option-${optionId}`}
                    >
                      <RadioGroupItem value={optIdx.toString()} id={`${question.question_id}-${optIdx}`} className="text-indigo-600" />
                      <Label htmlFor={`${question.question_id}-${optIdx}`} className="flex-1 cursor-pointer text-slate-700">
                        <span className="font-medium text-slate-500 mr-2">{String.fromCharCode(65 + optIdx)}.</span>
                        {optionText}
                      </Label>
                    </label>
                  );
                })}
              </div>
            </RadioGroup>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ExamPage;
